import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from './firebase.config.js';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const AgencyContext = createContext();

export const useAgency = () => {
  const context = useContext(AgencyContext);
  if (!context) {
    throw new Error('useAgency must be used within an AgencyProvider');
  }
  return context;
};

export const AgencyProvider = ({ children }) => {
  const [user] = useAuthState(auth);
  const [currentAgency, setCurrentAgency] = useState(null);
  const [userAgencies, setUserAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (user) {
      checkUserAgencies();
    } else {
      setCurrentAgency(null);
      setUserAgencies([]);
      setLoading(false);
      setNeedsOnboarding(false);
    }
  }, [user]);

  // Surveiller les changements dans userAgencies pour sortir du mode onboarding
  useEffect(() => {
    if (userAgencies.length > 0 && needsOnboarding) {
      console.log('User has agencies, exiting onboarding mode');
      setNeedsOnboarding(false);
      
      // Sélectionner automatiquement la première agence
      const savedAgencyKey = localStorage.getItem('currentAgencyKey');
      const selectedAgency = userAgencies.find(a => a.key === savedAgencyKey) || userAgencies[0];
      setCurrentAgency(selectedAgency);
    }
  }, [userAgencies, needsOnboarding]);

  const checkUserAgencies = async () => {
    try {
      setLoading(true);
      
      // Récupérer toutes les agences
      const agenciesSnapshot = await getDocs(collection(db, 'agencies'));
      const userAgenciesList = [];
      let invitedToAgencies = [];
      
      for (const agencyDoc of agenciesSnapshot.docs) {
        const agencyKey = agencyDoc.id;
        
        try {
          // Récupérer les données d'agence depuis settings/agency_info
          const agencyInfoDoc = await getDoc(doc(db, 'agencies', agencyKey, 'settings', 'agency_info'));
          if (!agencyInfoDoc.exists()) continue;
          
          const agencyData = agencyInfoDoc.data();
          
          // Vérifier si l'utilisateur est dans authorized_users de cette agence
          const userDocId = user.email.replace(/[^a-zA-Z0-9]/g, '_');
          const userDoc = await getDoc(doc(db, 'agencies', agencyKey, 'authorized_users', userDocId));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.status === 'active') {
              userAgenciesList.push({
                key: agencyKey,
                ...agencyData,
                userRole: userData.role
              });
            } else if (userData.status === 'invited' && userData.email === user.email) {
              // Utilisateur invité - automatiquement l'activer
              invitedToAgencies.push({
                key: agencyKey,
                ...agencyData,
                userRole: userData.role,
                userDocId: userDocId
              });
            }
          }
        } catch (error) {
          console.log(`No access to agency ${agencyKey}`);
        }
      }
      
      // Activer automatiquement les utilisateurs invités
      for (const invitedAgency of invitedToAgencies) {
        try {
          await setDoc(doc(db, 'agencies', invitedAgency.key, 'authorized_users', invitedAgency.userDocId), {
            email: user.email,
            role: invitedAgency.userRole,
            name: user.displayName || user.email,
            addedAt: serverTimestamp(),
            addedBy: 'system',
            status: 'active',
            activatedAt: serverTimestamp()
          }, { merge: true });
          
          userAgenciesList.push({
            key: invitedAgency.key,
            name: invitedAgency.name,
            userRole: invitedAgency.userRole
          });
        } catch (error) {
          console.error(`Error activating user for agency ${invitedAgency.key}:`, error);
        }
      }
      
      setUserAgencies(userAgenciesList);
      
      if (userAgenciesList.length === 0) {
        setNeedsOnboarding(true);
        setCurrentAgency(null);
      } else {
        // Sélectionner automatiquement la première agence ou celle sauvegardée
        const savedAgencyKey = localStorage.getItem('currentAgencyKey');
        const selectedAgency = userAgenciesList.find(a => a.key === savedAgencyKey) || userAgenciesList[0];
        setCurrentAgency(selectedAgency);
        setNeedsOnboarding(false);
      }
      
    } catch (error) {
      console.error('Error checking user agencies:', error);
      setNeedsOnboarding(true);
    } finally {
      setLoading(false);
    }
  };

  const selectAgency = (agency) => {
    setCurrentAgency(agency);
    localStorage.setItem('currentAgencyKey', agency.key);
  };

  const onboardingComplete = async (agencyKey) => {
    console.log('Onboarding complete for agency:', agencyKey);
    
    // Forcer le rechargement des agences immédiatement
    await checkUserAgencies();
  };

  // Helper function pour obtenir le chemin de collection avec l'agence
  const getAgencyCollection = useCallback((collectionName) => {
    if (!currentAgency) return null;
    return collection(db, 'agencies', currentAgency.key, collectionName);
  }, [currentAgency]);

  // Helper function pour obtenir une référence de document avec l'agence
  const getAgencyDoc = useCallback((collectionName, docId) => {
    if (!currentAgency) return null;
    return doc(db, 'agencies', currentAgency.key, collectionName, docId);
  }, [currentAgency]);

  const value = {
    currentAgency,
    userAgencies,
    loading,
    needsOnboarding,
    selectAgency,
    onboardingComplete,
    getAgencyCollection,
    getAgencyDoc,
    refreshAgencies: checkUserAgencies,
    hasExistingAgency: userAgencies.length > 0
  };

  return (
    <AgencyContext.Provider value={value}>
      {children}
    </AgencyContext.Provider>
  );
};

export default AgencyContext;